import React, { useState, useMemo, useEffect } from "react";
import {
  Eye,
  Trash2,
  Share,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Copy,
  FileText,
  X,
  AlertTriangle,
  ClipboardCheck,
  Truck,
  Ship,
  Plane,
} from "lucide-react";
import { Shipment, Column } from "../../types";
import CMRSharingModal from "../modals/CMRSharingModal";
import ReclamationSection from "./ReclamationSection";
import PickupHandoverModal from "./PickupHandoverModal";
import POHDocumentModal from "./POHDocumentModal";
import POHStatusDots from "../poh/POHStatusDots";
import { pohAPI } from "../../services/api";
import { createCmrPdf } from "../../utils/pdfGenerator";
import { createDeliveryNotePdf } from "../../utils/generateDeliveryNote";
import { createBillOfLadingPdf } from "../../utils/generateBillOfLading";
import { createAirWaybillPdf } from "../../utils/generateAirWaybill";
import { createShipmentOrderPdf } from "../../utils/generateShipmentOrder";
import {
  isShipmentDocumentReady,
  isShipmentBolReady,
  isShipmentAwbReady,
} from "../../utils/shipmentDocumentStatus";
import { useAppSelector } from "../../hooks/useStore";
import { STATUS_STEPS } from "../../constants/formOptions";
import { toast } from "react-toastify";
import { useTooltip } from "../app/TooltipManager";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import ActionMenu, { ActionMenuItem } from "../UI/ActionMenu";
import { columnDisplayName } from "../../utils/shipmentColumns";
import { vehicleRefLabel } from "../../utils/vehicleLabel";

interface ShipmentTableProps {
  shipments: Shipment[];
  selectedIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: string, checked: boolean) => void;
  onEdit: (user: Shipment) => void;
  onDelete: (id: string) => void;
  loading: boolean;
  handleCopyShipment: (id: string) => Promise<void>;
  columns: Column[];
  /** Shipment whose reclamation panel should open immediately (e.g. from a
   *  reclamation notification). The row need not be on the current page. */
  focusReclamation?: ReclamationPanelTarget | null;
  onFocusReclamationClose?: () => void;
}

/** Minimum a shipment needs for the reclamation side panel — a full `Shipment`
 *  row satisfies it, so does the id/number pair carried by a notification. */
type ReclamationPanelTarget = {
  _id: string;
  shipmentNumber: string;
  reclamationSummary?: Shipment["reclamationSummary"];
};

type SortField =
  | "id"
  | "createdBy"
  | "status"
  | "referenceNumber"
  | "from"
  | "pickupDate"
  | "to"
  | "deliveryDate"
  | "weight"
  | "assignedDriver"
  | "truckLicensePlate"
  | "trailerLicensePlate"
  | "groupNumber"
  | "loadType"
  | "direction"
  | "type"
  | "transportMode"
  | "client";

type SortDirection = "asc" | "desc";

// ── Shipment row action menu (built from shared ActionMenu) ───────────────────
interface ShipmentActionMenuProps {
  shipment: Shipment;
  onEdit: (s: Shipment) => void;
  onDelete: (id: string) => void;
  onCopy: (id: string) => void;
  onPreview: (s: Shipment) => void;
  onShare: (s: Shipment) => void;
  onViewReclamations: (s: Shipment) => void;
  onPickupHandover: (s: Shipment) => void;
  onShipmentOrder: (s: Shipment) => void;
}

const ShipmentActionMenu: React.FC<ShipmentActionMenuProps> = ({
  shipment,
  onEdit,
  onDelete,
  onCopy,
  onPreview,
  onShare,
  onViewReclamations,
  onPickupHandover,
  onShipmentOrder,
}) => {
  const { t } = useTranslation("shipments");

  const items: ActionMenuItem[] = [
    {
      label: t("table.edit"),
      icon: <Eye className="w-4 h-4" />,
      onClick: () => onEdit(shipment),
    },
    {
      label: t("table.copy"),
      icon: <Copy className="w-4 h-4" />,
      onClick: () => onCopy(shipment._id),
    },
    {
      label:
        (shipment.transportMode ?? "ROAD") === "SEA"
          ? t("table.previewBol")
          : (shipment.transportMode ?? "ROAD") === "AIR"
            ? t("table.previewAwb")
            : shipment.type === "Domestic"
              ? t("table.previewDeliveryNote")
              : t("table.previewCmr"),
      icon: <FileText className="w-4 h-4" />,
      onClick: () => onPreview(shipment),
      // Available only when the shipment has enough data to actually produce
      // the document — driven by the same Yup schema as the form-level
      // "Validate CMR / Delivery Note / BOL / AWB" button. Sea uses the BOL
      // gate, air the AWB gate.
      hidden:
        (shipment.transportMode ?? "ROAD") === "SEA"
          ? !isShipmentBolReady(shipment)
          : (shipment.transportMode ?? "ROAD") === "AIR"
            ? !isShipmentAwbReady(shipment)
            : !isShipmentDocumentReady(shipment),
    },
    {
      label: t("table.share"),
      icon: <Share className="w-4 h-4" />,
      onClick: () => onShare(shipment),
    },
    {
      label: "Pickup Handover",
      icon: <ClipboardCheck className="w-4 h-4" />,
      onClick: () => onPickupHandover(shipment),
      hidden: shipment.pohLevel === 0,
    },
    {
      label: "Reclamations",
      icon: (
        <span className="relative">
          <AlertTriangle className="w-4 h-4" />
          {(shipment.reclamationSummary?.total ?? 0) > 0 && (
            <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${
              (shipment.reclamationSummary?.open ?? 0) > 0 ? "bg-amber-500" : "bg-gray-400"
            }`} />
          )}
        </span>
      ),
      onClick: () => onViewReclamations(shipment),
      className:
        (shipment.reclamationSummary?.open ?? 0) > 0
          ? "text-amber-600 hover:bg-amber-50"
          : (shipment.reclamationSummary?.total ?? 0) > 0
          ? "text-gray-600 hover:bg-gray-50"
          : "text-gray-700 hover:bg-gray-50",
    },
    {
      label: "SO Document",
      icon: <FileText className="w-4 h-4" />,
      onClick: () => onShipmentOrder(shipment),
    },
    {
      label: t("table.delete"),
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () => onDelete(shipment._id),
      variant: "danger",
      divider: true,
    },
  ];

  return <ActionMenu items={items} ariaLabel={t("table.actions")} />;
};

// ── Main table ────────────────────────────────────────────────────────────────
const ShipmentTable: React.FC<ShipmentTableProps> = ({
  shipments,
  onEdit,
  onDelete,
  loading,
  handleCopyShipment,
  columns,
  focusReclamation,
  onFocusReclamationClose,
}) => {
  const { t } = useTranslation("shipments");
  const [sortField, setSortField] = useState<SortField>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const [sharingDocumentId, setSharingDocumentId] = useState<Shipment | null>(
    null,
  );
  const { countries } = useAppSelector((state) => state.company);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewShipmentId, setPreviewShipmentId] = useState<string | null>(null);
  // Which document the preview modal is currently showing: the mode document
  // (CMR/BOL/AWB/Delivery Note) or the cross-mode Shipment Order.
  const [previewKind, setPreviewKind] = useState<"document" | "shipmentOrder">("document");
  const [reclamationShipment, setReclamationShipment] =
    useState<ReclamationPanelTarget | null>(null);
  const [pickupHandoverShipment, setPickupHandoverShipment] = useState<Shipment | null>(null);
  const [pohData, setPohData] = useState<{ poh: Record<string, unknown>; shipmentId: string; pohLevel: 0 | 1 | 2; driverSignUrl: string | null; deliveryDriverSignUrl: string | null } | null>(null);
  const [pohFetching, setPohFetching] = useState(false);

  // Open the panel when a reclamation notification points at a shipment.
  // Prefer the loaded row (it carries the open/total badge counts); fall back
  // to the id/number pair when that shipment isn't on the current page.
  useEffect(() => {
    if (!focusReclamation) return;
    const row = shipments.find((s) => s._id === focusReclamation._id);
    setReclamationShipment(row ?? focusReclamation);
  }, [focusReclamation, shipments]);

  const closeReclamationPanel = () => {
    setReclamationShipment(null);
    onFocusReclamationClose?.();
  };

  const handlePickupHandover = async (shipment: Shipment) => {
    setPohFetching(true);
    try {
      const res = await pohAPI.getPohByShipment(shipment._id);
      const { poh, driverSignUrl, deliveryDriverSignUrl } = res.data.data;
      if (poh) {
        setPohData({ poh, shipmentId: shipment._id, pohLevel: shipment.pohLevel, driverSignUrl: driverSignUrl ?? null, deliveryDriverSignUrl: deliveryDriverSignUrl ?? null });
      } else {
        setPickupHandoverShipment(shipment);
      }
    } catch {
      setPickupHandoverShipment(shipment);
    } finally {
      setPohFetching(false);
    }
  };

  const { showTooltip, hideTooltip } = useTooltip();

  const visibleColumns = useMemo(() => {
    return columns.filter((column) => column.visible);
  }, [columns]);

  function safeDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date && !isNaN(value.getTime())) return value;
    if (typeof value === "number") {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === "string") {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  const sortedShipments = useMemo(() => {
    const sorted = [...shipments].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case "id":
          aValue = a.shipmentNumber;
          bValue = b.shipmentNumber;
          break;
        case "createdBy":
          aValue = `${a.createdBy?.firstName} ${a.createdBy?.lastName}`;
          bValue = `${b.createdBy?.firstName} ${b.createdBy?.lastName}`;
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "referenceNumber":
          aValue = a.referenceNumber || "";
          bValue = b.referenceNumber || "";
          break;
        case "from":
          aValue = a.pickup?.companyName || "";
          bValue = b.pickup?.companyName || "";
          break;
        case "pickupDate":
          aValue = safeDate(a.pickup?.date);
          bValue = safeDate(b.pickup?.date);
          break;
        case "to":
          aValue = a.delivery?.companyName || "";
          bValue = b.delivery?.companyName || "";
          break;
        case "deliveryDate":
          aValue = safeDate(a.delivery?.date);
          bValue = safeDate(b.delivery?.date);
          break;
        case "truckLicensePlate":
          aValue = vehicleRefLabel(a.carrier?.truckLicensePlate);
          bValue = vehicleRefLabel(b.carrier?.truckLicensePlate);
          break;
        case "assignedDriver":
          aValue =
            typeof a.assignedDriver === "object"
              ? `${a.assignedDriver?.firstName} ${a.assignedDriver?.lastName}`
              : a.assignedDriver;
          bValue =
            typeof b.assignedDriver === "object"
              ? `${b.assignedDriver?.firstName} ${b.assignedDriver?.lastName}`
              : b.assignedDriver;
          break;
        case "groupNumber":
          aValue =
            typeof a.groupId?.groupNumber === "object"
              ? a.groupId?.groupNumber
              : a.groupId;
          bValue =
            typeof b.groupId?.groupNumber === "object"
              ? b.groupId?.groupNumber
              : b.groupId;
          break;
        case "trailerLicensePlate":
          aValue = vehicleRefLabel(a.carrier?.trailerLicensePlate);
          bValue = vehicleRefLabel(b.carrier?.trailerLicensePlate);
          break;
        case "weight":
          aValue = a.totalWeight;
          bValue = b.totalWeight;
          break;
        case "loadType":
          aValue = a.loadType;
          bValue = b.loadType;
          break;
        case "direction":
          aValue = a.loadType;
          bValue = b.loadType;
          break;
        case "type":
          aValue = a.type;
          bValue = b.type;
          break;
        case "transportMode":
          aValue = a.transportMode || "ROAD";
          bValue = b.transportMode || "ROAD";
          break;
        case "client":
          aValue = a.client?.companyName || "";
          bValue = b.client?.companyName || "";
          break;
        default:
          return 0;
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        return sortDirection === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue ?? "").toLowerCase();
      const bStr = String(bValue ?? "").toLowerCase();

      return sortDirection === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return sorted;
  }, [shipments, sortField, sortDirection]);

  const formatDate = (value: any) => {
    const d = safeDate(value);
    if (!d) return "-";
    return d.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    const colors = {
      Order: "bg-gray-100 text-gray-800",
      AwaitingPickup: "bg-orange-100 text-orange-800",
      InTransit: "bg-blue-100 text-blue-800",
      Completed: "bg-green-100 text-green-800",
      other: "bg-gray-100 text-gray-800",
    };
    return colors[status as keyof typeof colors] || colors.other;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4 text-blue-600" />
    ) : (
      <ArrowDown className="h-4 w-4 text-blue-600" />
    );
  };

  const formatTimestamp = (timestamp: string | number | Date) => {
    const date =
      timestamp instanceof Date
        ? timestamp
        : new Date(
            typeof timestamp === "number"
              ? timestamp
              : Number(timestamp) || timestamp,
          );
    return date.toLocaleString();
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "Domestic":
        return "bg-blue-100 text-blue-800";
      case "International":
        return "bg-purple-100 text-purple-800";
      case "Mixed":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTransportModeColor = (mode: string) => {
    switch (mode) {
      case "SEA":
        return "bg-blue-100 text-blue-800";
      case "AIR":
        return "bg-sky-100 text-sky-800";
      case "ROAD":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTransportModeIcon = (mode: string) => {
    switch (mode) {
      case "SEA":
        return <Ship className="h-3.5 w-3.5" />;
      case "AIR":
        return <Plane className="h-3.5 w-3.5" />;
      default:
        return <Truck className="h-3.5 w-3.5" />;
    }
  };

  const getDirectionIcon = (direction: string | undefined) => {
    if (direction === "Inbound") {
      return (
        <span className="flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
          {t("table.inbound")}
        </span>
      );
    }
    if (direction === "Outbound") {
      return (
        <span className="flex items-center px-3 py-1 rounded-full bg-green-100 text-green-600 text-sm font-medium">
          {t("table.outbound")}
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-500 text-sm">
        -
      </span>
    );
  };

  const getJourneyTooltip = (shipment: Shipment) => {
    const { driverAccess } = shipment;

    if (!driverAccess.journeyStarted) {
      return (
        <div className="text-xs bg-gray-900 text-white rounded-lg px-3 py-2">
          <div>
            {t("table.journeyStart")}: {t("table.pending")}
          </div>
          <div>
            {t("table.journeyEnd")}: {t("table.pending")}
          </div>
        </div>
      );
    } else if (driverAccess.journeyStarted && !driverAccess.journeyCompleted) {
      return (
        <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2">
          {driverAccess.journeyStartTime ? (
            <div>
              <div className="font-medium">{t("table.journeyStart")}:</div>
              <div>{formatTimestamp(driverAccess.journeyStartTime)}</div>
              <div>
                {t("table.at")} {shipment.pickup?.address?.city || "-"}
              </div>
            </div>
          ) : (
            <div>{t("table.journeyStarted")}</div>
          )}
          <div>
            {t("table.journeyEnd")}: {t("table.pending")}
          </div>
        </div>
      );
    } else if (driverAccess.journeyStarted && driverAccess.journeyCompleted) {
      return (
        <div className="text-xs bg-gray-900 text-white rounded-lg px-3 py-2">
          {driverAccess.journeyStartTime ? (
            <div>
              <div className="font-medium">{t("table.journeyStarted")}:</div>
              <div>{formatTimestamp(driverAccess.journeyStartTime)}</div>
              <div>
                {t("table.at")} {shipment.pickup?.address?.city || "-"}
              </div>
            </div>
          ) : (
            <div>
              {t("table.journeyStart")}: {t("table.pending")}
            </div>
          )}
          {driverAccess.journeyCompletedTime ? (
            <div>
              <div className="font-medium">{t("table.journeyCompleted")}:</div>
              <div>{formatTimestamp(driverAccess.journeyCompletedTime)}</div>
              <div>
                {t("table.at")} {shipment.delivery?.address?.city || "-"}
              </div>
            </div>
          ) : (
            <div>{t("table.journeyCompleted")}</div>
          )}
        </div>
      );
    }
  };

  const handleShareShipment = (shipment: Shipment) => {
    setSharingDocumentId(shipment);
    setIsSharingModalOpen(true);
  };

  const handleCloseSharingModal = () => {
    setIsSharingModalOpen(false);
    setSharingDocumentId(null);
  };

  const handlePreviewShipment = async (shipment: Shipment) => {
    setPreviewKind("document");
    setPreviewShipmentId(shipment._id);
    setIsPreviewOpen(true);
    await regeneratePreview(shipment, "document");
  };

  // Shipment Order is a cross-mode document available for every shipment.
  const handleShipmentOrder = async (shipment: Shipment) => {
    setPreviewKind("shipmentOrder");
    setPreviewShipmentId(shipment._id);
    setIsPreviewOpen(true);
    await regeneratePreview(shipment, "shipmentOrder");
  };

  const regeneratePreview = async (
    shipment: Shipment,
    kind: "document" | "shipmentOrder" = previewKind,
  ) => {
    try {
      if (!shipment) return;

      let pdfBytes: Uint8Array | undefined;

      if (kind === "shipmentOrder") {
        pdfBytes = await createShipmentOrderPdf(shipment, countries);
      } else if ((shipment.transportMode ?? "ROAD") === "SEA") {
        pdfBytes = await createBillOfLadingPdf(shipment, countries);
      } else if ((shipment.transportMode ?? "ROAD") === "AIR") {
        pdfBytes = await createAirWaybillPdf(shipment, countries);
      } else if (shipment.type === "Domestic") {
        pdfBytes = await createDeliveryNotePdf(shipment);
      } else if (shipment.type === "International") {
        pdfBytes = await createCmrPdf(shipment, countries);
      }

      if (!pdfBytes) {
        console.warn("PDF generation returned undefined");
        toast.error(t("table.failedToGeneratePreview"));
        return;
      }

      const safeBytes = new Uint8Array(pdfBytes);
      const blob = new Blob([safeBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
    } catch (e) {
      console.error("Failed to regenerate preview:", e);
      toast.error(t("table.failedToGeneratePreviewShort"));
    }
  };

  function toSentenceCase(text: string) {
    if (!text) return "";
    text = text.toLowerCase();
    return text.replace(/([a-zA-Z])/, (match) => match.toUpperCase());
  }

  // Net (excluding-tax) sum of a service list: Σ price × qty. Mirrors the
  // read-only view, which treats price × qty as the tax-exclusive line total.
  const sumExclTax = (rows?: { price: number; qty: number }[]) =>
    (rows ?? []).reduce((acc, r) => acc + (r.price || 0) * (r.qty || 0), 0);

  const renderCellContent = (
    columnId: string,
    shipment: Shipment,
    rowIndex: number,
  ) => {
    // Alternating row base: even rows white, odd rows light gray
    const stripe = rowIndex % 2 === 0 ? "" : "bg-gray-50";

    const cellClass = `px-4 py-2 whitespace-nowrap text-sm text-gray-600 border border-gray-200 ${stripe}`;

    switch (columnId) {
      case "id":
        return (
          <td
            className={`px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 border border-gray-200 ${stripe}`}
          >
            <div className="flex items-center gap-2">
              <span>{shipment.shipmentNumber}</span>
              {(shipment as unknown as { pohSummary?: any }).pohSummary && shipment.pohLevel !== 0 && (
                <POHStatusDots poh={(shipment as unknown as { pohSummary: any }).pohSummary} pohLevel={shipment.pohLevel} />
              )}
            </div>
          </td>
        );
      case "createdBy":
        return (
          <td className={cellClass}>
            {shipment?.createdBy?.firstName?.[0] || ""}.
            {shipment?.createdBy?.lastName?.[0] || ""}
          </td>
        );
      case "status":
        return (
          <td
            className={`px-4 py-2 whitespace-nowrap border border-gray-200 ${stripe}`}
          >
            <div className="flex flex-col gap-1">
              <div
                className="relative inline-block"
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  showTooltip(
                    getJourneyTooltip(shipment),
                    rect.left + rect.width / 2,
                    rect.top - 8,
                  );
                }}
                onMouseLeave={hideTooltip}
              >
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-help ${getStatusColor(shipment.status)}`}
                >
                  {STATUS_STEPS.find((status) => status.key === shipment.status)
                    ?.label || shipment.status}
                </span>
              </div>
              {(shipment.reclamationSummary?.total ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setReclamationShipment(shipment);
                  }}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full transition-colors w-fit ${
                    (shipment.reclamationSummary?.open ?? 0) > 0
                      ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <AlertTriangle className="h-3 w-3" />
                  {shipment.reclamationSummary!.total} reclaim
                </button>
              )}
            </div>
          </td>
        );
      case "pohLevel":
        return (
          <td className={`${cellClass} whitespace-nowrap`}>
            {[0, 1, 2].includes(shipment.pohLevel) ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                Level {shipment.pohLevel}
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </td>
        );
      case "referenceNumber":
        return (
          <td className={cellClass}>{shipment?.referenceNumber || "-"}</td>
        );
      case "from":
        return (
          <td
            className={`px-4 py-2 text-sm text-gray-600 border border-gray-200 ${stripe}`}
          >
            <div className="flex flex-col gap-1">
              <div className="font-medium truncate">
                {shipment?.pickup?.companyName || "-"}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                {typeof shipment?.pickup?.address?.country === "object" && (
                  <ReactCountryFlag
                    countryCode={shipment?.pickup?.address?.country?.code}
                    svg
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "9999px",
                      flexShrink: 0,
                    }}
                  />
                )}
                <span className="wrap-break-word">
                  {toSentenceCase(
                    (
                      shipment?.pickup?.address?.street?.split(",")[0] ||
                      shipment?.pickup?.address?.city ||
                      shipment?.pickup?.address?.state ||
                      shipment?.pickup?.address?.postalCode ||
                      ""
                    )?.toLowerCase() || t("table.addressNotAvailable"),
                  )}
                </span>
              </div>
            </div>
          </td>
        );
      case "pickupDate":
        return (
          <td className={cellClass}>
            {formatDate(shipment?.pickup?.date ?? null)}
          </td>
        );
      case "to":
        return (
          <td
            className={`px-4 py-2 text-sm text-gray-600 border border-gray-200 ${stripe}`}
          >
            <div className="flex flex-col gap-1">
              <div className="font-medium truncate">
                {shipment?.delivery?.companyName || "-"}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                {typeof shipment?.delivery?.address?.country === "object" && (
                  <ReactCountryFlag
                    countryCode={shipment?.delivery?.address?.country?.code}
                    svg
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "9999px",
                      flexShrink: 0,
                    }}
                  />
                )}
                <span className="wrap-break-word">
                  {toSentenceCase(
                    (
                      shipment?.delivery?.address?.street?.split(",")[0] ||
                      shipment?.delivery?.address?.city ||
                      shipment?.delivery?.address?.state ||
                      shipment?.delivery?.address?.postalCode ||
                      ""
                    )?.toLowerCase() || t("table.addressNotAvailable"),
                  )}
                </span>
              </div>
            </div>
          </td>
        );
      case "deliveryDate":
        return (
          <td className={cellClass}>
            {formatDate(shipment?.delivery?.date ?? null)}
          </td>
        );
      case "truckLicensePlate":
        return (
          <td className={cellClass}>
            {vehicleRefLabel(shipment?.carrier?.truckLicensePlate) || "-"}
          </td>
        );
      case "assignedDriver":
        const hasDriver =
          shipment?.assignedDriver &&
          (typeof shipment.assignedDriver === "object"
            ? shipment.assignedDriver !== null
            : true);
        return (
          <td className={cellClass}>
            <span className="mr-2">
              {typeof shipment?.assignedDriver === "object" &&
              shipment?.assignedDriver !== null
                ? `${shipment?.assignedDriver?.firstName} ${shipment?.assignedDriver?.lastName}`
                : shipment?.assignedDriver || "-"}
            </span>
            {hasDriver && shipment?.assignedDriver !== "-" && (
              <>
                /{" "}
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    shipment.assignedToDriverApp
                      ? "bg-purple-100 text-purple-700"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {shipment.assignedToDriverApp ? "Assigned" : "Not Assigned"}
                </span>
              </>
            )}
          </td>
        );
      case "groupNumber":
        return (
          <td className={cellClass}>
            {typeof shipment?.groupId === "object"
              ? shipment?.groupId?.groupNumber
              : shipment?.groupId || "-"}
          </td>
        );
      case "trailerLicensePlate":
        return (
          <td className={cellClass}>
            {vehicleRefLabel(shipment?.carrier?.trailerLicensePlate) || "-"}
          </td>
        );
      case "weight":
        return <td className={cellClass}>{shipment.totalWeight || 0}</td>;
      case "loadType":
        return <td className={cellClass}>{shipment.loadType || "-"}</td>;
      case "direction":
        return (
          <td
            className={`px-4 py-2 whitespace-nowrap text-sm text-gray-600 border border-gray-200 ${stripe}`}
          >
            {getDirectionIcon(shipment.direction)}
          </td>
        );
      case "transportMode": {
        const mode = shipment.transportMode || "ROAD";
        return (
          <td
            className={`px-4 py-2 whitespace-nowrap text-sm text-gray-600 border border-gray-200 ${stripe}`}
          >
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getTransportModeColor(mode)}`}
            >
              {getTransportModeIcon(mode)}
              {t(`transportMode.${mode.toLowerCase()}`)}
            </span>
          </td>
        );
      }
      case "type": {
        // Type (International / Domestic) is a Road-only concept. Sea uses a
        // BOL and Air an AWB, so there's no shipment "type" to show there.
        const isRoad = (shipment.transportMode || "ROAD") === "ROAD";
        return (
          <td
            className={`px-4 py-2 whitespace-nowrap text-sm text-gray-600 border border-gray-200 ${stripe}`}
          >
            {isRoad ? (
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(shipment.type)}`}
              >
                {shipment.type || "-"}
              </span>
            ) : (
              "-"
            )}
          </td>
        );
      }
      case "client":
        return (
          <td className={cellClass}>
            <div className="flex flex-col gap-0.5">
              <div className="font-medium truncate text-gray-700">
                {shipment?.client?.companyName || "-"}
              </div>
              {shipment?.client?.clientCode && (
                <div className="text-xs text-gray-500 truncate">
                  {shipment.client.clientCode}
                </div>
              )}
            </div>
          </td>
        );
      case "linkedTask": {
        const lt = shipment.linkedTaskId;
        if (!lt) return <td className={cellClass}>—</td>;
        const taskNum = typeof lt === "object" ? lt.taskNumber : lt;
        const taskDesc = typeof lt === "object" ? lt.description : undefined;
        return (
          <td className={cellClass}>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 truncate max-w-30"
              title={taskDesc}
            >
              {taskNum}
            </span>
          </td>
        );
      }
      case "invoicingExclTax":
        return (
          <td className={cellClass}>
            €{sumExclTax(shipment.services).toFixed(2)}
          </td>
        );
      case "costExclTax":
        return (
          <td className={cellClass}>
            €{sumExclTax(shipment.costServices).toFixed(2)}
          </td>
        );
      case "netProfitExclTax": {
        const net =
          sumExclTax(shipment.services) - sumExclTax(shipment.costServices);
        return (
          <td
            className={`px-4 py-2 whitespace-nowrap text-sm font-medium border border-gray-200 ${stripe} ${
              net < 0 ? "text-red-600" : "text-gray-700"
            }`}
          >
            €{net.toFixed(2)}
          </td>
        );
      }
      case "actions":
        return (
          <td
            className={`px-4 py-2 whitespace-nowrap text-sm border border-gray-200 ${stripe}`}
          >
            <ShipmentActionMenu
              shipment={shipment}
              onEdit={onEdit}
              onDelete={onDelete}
              onCopy={handleCopyShipment}
              onPreview={handlePreviewShipment}
              onShare={handleShareShipment}
              onViewReclamations={(s) => setReclamationShipment(s)}
              onPickupHandover={handlePickupHandover}
              onShipmentOrder={handleShipmentOrder}
            />
          </td>
        );
      default:
        return <td className={cellClass} />;
    }
  };

  const renderHeaderContent = (column: Column) => {
    const isClickable = [
      "id",
      "createdBy",
      "status",
      "referenceNumber",
      "from",
      "pickupDate",
      "to",
      "deliveryDate",
      "assignedDriver",
      "truckLicensePlate",
      "trailerLicensePlate",
      "weight",
      "loadType",
      "direction",
      "type",
      "transportMode",
      "client",
    ].includes(column.id);

    if (!isClickable) {
      return (
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
          {column.name === "Direction"
            ? t("table.inOut")
            : columnDisplayName(column)}
        </th>
      );
    }

    return (
      <th
        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200"
        onClick={() => handleSort(column.id as SortField)}
      >
        <div className="flex items-center space-x-1">
          <span>
            {column.name === "Direction" ? t("table.inOut") : column.name}
          </span>
          {getSortIcon(column.id as SortField)}
        </div>
      </th>
    );
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 border-b border-gray-200"></div>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`h-16 border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[50svh] md:max-h-[55vh] lg:max-h-[60vh] xl:max-h-[70vh] overflow-y-scroll">
      <table className="min-w-full border-collapse border border-gray-300">
        <thead className="bg-gray-50 text-nowrap sticky top-0 z-10">
          <tr>
            {visibleColumns.map((column) => (
              <React.Fragment key={column.id}>
                {renderHeaderContent(column)}
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {sortedShipments.map((shipment, rowIndex) => (
            <tr
              onClick={() => onEdit(shipment)}
              key={shipment._id}
              className={`transition-colors duration-150 cursor-pointer hover:bg-blue-50 ${
                rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
              }`}
            >
              {visibleColumns.map((column) => (
                <React.Fragment key={`${shipment._id}-${column.id}`}>
                  {renderCellContent(column.id, shipment, rowIndex)}
                </React.Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {shipments.length === 0 && !loading && (
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("table.noShipmentsFound")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{t("table.getStarted")}</p>
        </div>
      )}

      {/* ── Reclamation detail panel ── */}
      {reclamationShipment && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-end">
          <div className="bg-white w-full max-w-xl h-full flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h2 className="font-bold text-gray-900">Reclamations</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Shipment {reclamationShipment.shipmentNumber}
                  {(reclamationShipment.reclamationSummary?.open ?? 0) > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                      <AlertTriangle className="h-3 w-3" />
                      {reclamationShipment.reclamationSummary!.open} open
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={closeReclamationPanel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              <ReclamationSection shipmentId={reclamationShipment._id} />
            </div>
          </div>
        </div>
      )}

      {isSharingModalOpen && sharingDocumentId && (
        <CMRSharingModal
          key={sharingDocumentId._id + sharingDocumentId.waybill?.status}
          documentId={sharingDocumentId}
          isOpen={true}
          onClose={handleCloseSharingModal}
        />
      )}

      {isPreviewOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-dark-600" />
                <span className="text-sm font-medium text-dark-800">
                  {(() => {
                    if (previewKind === "shipmentOrder")
                      return "Shipment Order Preview";
                    const s = shipments.find(
                      (sh) => sh._id === previewShipmentId,
                    );
                    if ((s?.transportMode ?? "ROAD") === "SEA")
                      return t("table.bolPreview");
                    if ((s?.transportMode ?? "ROAD") === "AIR")
                      return t("table.awbPreview");
                    return s?.type === "International"
                      ? t("table.cmrPreview")
                      : t("table.deliveryNotePreview");
                  })()}{" "}
                  {previewShipmentId ? `(${previewShipmentId})` : ""}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    if (previewShipmentId) {
                      const shipment = shipments.find(
                        (s) => s._id === previewShipmentId,
                      );
                      if (shipment) regeneratePreview(shipment);
                    }
                  }}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  {t("table.refresh")}
                </button>
                <button
                  onClick={() => {
                    setIsPreviewOpen(false);
                    setPreviewShipmentId(null);
                    setPreviewKind("document");
                    if (previewUrl) {
                      URL.revokeObjectURL(previewUrl);
                      setPreviewUrl(null);
                    }
                  }}
                  className="p-1 text-dark-600 hover:text-dark-900"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              {previewUrl ? (
                <iframe
                  title="CMR Preview"
                  src={previewUrl}
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-dark-500 text-sm">
                  {t("table.generatingPreview")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    {pickupHandoverShipment && (
      <PickupHandoverModal
        shipmentId={pickupHandoverShipment._id}
        shipmentNumber={pickupHandoverShipment.shipmentNumber}
        pohLevel={pickupHandoverShipment.pohLevel}
        onClose={() => setPickupHandoverShipment(null)}
      />
    )}
    {pohData && (
      <POHDocumentModal
        poh={pohData.poh}
        shipmentId={pohData.shipmentId}
        pohLevel={pohData.pohLevel}
        driverSignUrl={pohData.driverSignUrl}
        deliveryDriverSignUrl={pohData.deliveryDriverSignUrl}
        onClose={() => setPohData(null)}
      />
    )}
    {pohFetching && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
        <div className="bg-white rounded-xl shadow-xl p-5 flex items-center gap-3">
          <div className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-700">Loading handover document…</span>
        </div>
      </div>
    )}
    </div>
  );
};

export default ShipmentTable;
